import React, { useContext, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';

import AuthContext from 'app/src/contexts/AuthContext';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { currency } from 'app/src/utils/stringHelpers';
import { dateShort } from 'app/src/utils/timeFormatter';

import { TXNS_WITH_COUNT } from '../Txns/queries';
import SubNav from './SubNav';
import EditExpense from './edit';

const baseColumns = [
  {
    key: 'bankDate',
    label: 'Date',
    sort: 'desc',
    content: dateShort,
  },
  {
    key: 'createdAt',
    label: 'Date Entered',
    content: dateShort,
  },
  {
    key: 'type',
    label: 'Type',
  },
  {
    key: 'method',
    label: 'Payment Method',
  },
  {
    key: 'reference',
    label: 'Invoice Number',
  },
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'amount',
    label: 'Amount',
    content: ({ value }) => currency(value),
  },
  {
    key: 'account',
    label: 'Expense Account',
    sort: 'prevent',
    value: ({ entry }) => entry.txnLines[1]?.account.name,
  },
  {
    key: 'feeType',
    label: 'Fee Charged',
    sort: 'prevent',
    value: ({ entry }) => entry.txnLines.find(txnLine => txnLine.fee)?.fee.type,
  },
  /*
  {
    key: 'createdBy',
    label: 'Requested By',
    value: ({ value }) => value.fullName,
  },
  */
];

const Expenses = () => {
  const [showEditModal, setShowEditModal] = useState();
  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({
    order: [{key: 'bankDate', direction: 'desc'}],
  });

  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    order: settings.order,
    scopes: ['expense'],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const { accounts, role, currentUser } = useContext(AuthContext);

  const fees = ['Buy', 'Sell', 'Trans', 'Key', 'Recon', 'Mech', 'Inspection', 'Other', 'Storage', 'Listing', 'Bailout', 'Finanace Charge']

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer>
          <Pressable onPress={() => setShowEditModal(entry)}>
            <Text>Edit</Text>
          </Pressable>
        </LinkContainer>
      )
    },
  ];

  if (parseInt(currentUser?.id) === 10) {
    columns.push(
      {
        key: 'assets',
        label: 'Assets',
        content: ({ entry }) => (
          <>
            {entry.assets.map(asset => (
              <a
                href={`/sellers/${asset.seller.id}/assets/${asset.id}`}
              >
                {asset.lastSix} ({asset.listings})
              </a>
            ))}
          </>
        )
      },
      {
        key: 'soldOn',
        label: 'Sold?',
        value: ({ entry }) => entry.txnLines?.[1]?.asset?.soldOn,
        content: dateShort || null,
      },
    )
  }

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px'}}>
          <View style={{marginBottom: '20px'}}>
            <Filter.DateRange
              columnKey="bank_date"
              label="Date"
              filters={filters}
              setFilter={setFilter}
            />
          </View>
          <Text>Account:</Text>
          <select
            type={'select'}
            name="account_id"
            onChange={e => {
              setFilter('account_id', e.target.value)}
            }
            value={filters.account_id || ''}
          >
            <option key={`account-any`}>{'Any'}</option>
            {(accounts || []).map(account => (
              <option key={account.id} value={account.id}>
                {account.label}
              </option>
            ))}
          </select>
          <Text>Fee Type:</Text>
          <select
            type={'select'}
            name="fee_type"
            onChange={e => {
              setFilter('fee_type', e.target.value)}
            }
            value={filters.fee_type || ''}
          >
            <option key={`fee-any`}>{'Any'}</option>
            {
              fees.map(fee => (
              <option key={`fee-${fee}`} value={fee}>
                {fee}
              </option>
            ))}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      <SubNav refetch={refetch} />

      <ContentContainer>
        <PageTitle>Expenses</PageTitle>

        <DataTable.Gql
          setRefetch={setRefetch}
          query={TXNS_WITH_COUNT}
          queryName='txnsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.txnsWithCount)}
          htmlTable
        />
      </ContentContainer>

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Expense"
        >
          <EditExpense
            txn={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
          />
        </Modal>
      }
    </MainContainer>
  );
};

export default Expenses;
